import { EMPTY, BLACK, WHITE, type Color, type Vertex, type Rules } from './types.js';
import type { Position } from './position.js';

/** Who an empty point counts for. */
export const NEUTRAL = 0 as const;
export type Owner = 0 | Color;

export interface ScoreBreakdown {
  readonly blackStones: number;
  readonly whiteStones: number;
  /** Stones removed as dead before counting, by colour. */
  readonly blackDead: number;
  readonly whiteDead: number;
  readonly blackTerritory: number;
  readonly whiteTerritory: number;
  /** Empty points bordered by both colours — they count for nobody. */
  readonly dame: number;
  readonly komi: number;
  readonly black: number;
  readonly white: number;
  /** Positive means Black leads by that many points. */
  readonly margin: number;
  readonly winner: Color | null;
}

/** Stones agreed to be dead. They are off the board for counting purposes. */
export type DeadStones = ReadonlySet<Vertex> | readonly Vertex[];

function deadSet(dead: DeadStones | undefined): ReadonlySet<Vertex> {
  if (dead === undefined) return EMPTY_SET;
  return dead instanceof Set ? dead : new Set(dead);
}

const EMPTY_SET: ReadonlySet<Vertex> = new Set<Vertex>();

/**
 * Maps every empty point to the colour that surrounds it, or NEUTRAL when it is
 * bordered by both (or by nothing, on an empty board).
 *
 * Stones passed as `dead` are treated as though they had already been lifted:
 * the points under them become territory for the side that surrounded them, and
 * they stop walling anything off. Without that, a finished game with a dead
 * group still sitting on the board scores it as alive and as owning the space
 * it is standing on — which is wrong twice over.
 *
 * Left out, every stone counts as alive, which is exactly right for the
 * territory overlay during play, where nothing has been agreed yet.
 */
export function ownership(position: Position, dead?: DeadStones): Int8Array {
  const { area, nbr, off } = position.geo;
  const owner = new Int8Array(area);
  const visited = new Uint8Array(area);
  const gone = deadSet(dead);
  const at = (v: Vertex) => (gone.has(v) ? EMPTY : position.stones[v]);

  for (let start = 0; start < area; start++) {
    if (at(start) !== EMPTY || visited[start]) continue;

    const region: Vertex[] = [start];
    visited[start] = 1;
    let touchesBlack = false;
    let touchesWhite = false;

    for (let i = 0; i < region.length; i++) {
      const cur = region[i];
      for (let k = off[cur]; k < off[cur + 1]; k++) {
        const n = nbr[k];
        const s = at(n);
        if (s === EMPTY) {
          if (!visited[n]) {
            visited[n] = 1;
            region.push(n);
          }
        } else if (s === BLACK) {
          touchesBlack = true;
        } else {
          touchesWhite = true;
        }
      }
    }

    const value: Owner =
      touchesBlack && !touchesWhite ? BLACK : touchesWhite && !touchesBlack ? WHITE : NEUTRAL;
    for (const v of region) owner[v] = value;
  }
  return owner;
}

/**
 * Scores a position. `area` (Chinese) counts stones plus surrounded territory;
 * `territory` (Japanese) counts surrounded territory plus prisoners taken.
 *
 * Both assume all stones on the board are alive, so scores are only final after
 * both players have passed with dead stones removed.
 */
export function score(position: Position, rules: Rules, dead?: DeadStones): ScoreBreakdown {
  const gone = deadSet(dead);
  const owner = ownership(position, gone);
  let blackStones = 0;
  let whiteStones = 0;
  let blackDead = 0;
  let whiteDead = 0;
  let blackTerritory = 0;
  let whiteTerritory = 0;
  let dame = 0;

  for (let v = 0; v < position.area; v++) {
    const s = position.stones[v];
    // A dead stone counts for nobody as a stone, and the point it stands on is
    // territory for whoever surrounded it — the same as if it had been lifted.
    if (s !== EMPTY && gone.has(v)) {
      if (s === BLACK) blackDead++;
      else whiteDead++;
      if (owner[v] === BLACK) blackTerritory++;
      else if (owner[v] === WHITE) whiteTerritory++;
      else dame++;
      continue;
    }
    if (s === BLACK) blackStones++;
    else if (s === WHITE) whiteStones++;
    else if (owner[v] === BLACK) blackTerritory++;
    else if (owner[v] === WHITE) whiteTerritory++;
    else dame++;
  }

  // Under territory rules a dead stone is also a prisoner, on top of the point
  // it hands over. Under area rules it is not counted twice: losing the stone
  // and the point beneath it is already the whole swing.
  const blackPrisoners = position.captures[BLACK - 1] + whiteDead;
  const whitePrisoners = position.captures[WHITE - 1] + blackDead;

  const black =
    rules.scoring === 'area' ? blackStones + blackTerritory : blackTerritory + blackPrisoners;
  const white =
    (rules.scoring === 'area' ? whiteStones + whiteTerritory : whiteTerritory + whitePrisoners) +
    rules.komi;

  const margin = black - white;
  return {
    blackStones,
    whiteStones,
    blackDead,
    whiteDead,
    blackTerritory,
    whiteTerritory,
    dame,
    komi: rules.komi,
    black,
    white,
    margin,
    winner: margin > 0 ? BLACK : margin < 0 ? WHITE : null,
  };
}

/** `B+7.5` / `W+2.5` / `Draw` — the standard way results are written. */
export function formatResult(breakdown: ScoreBreakdown): string {
  if (breakdown.winner === null) return 'Draw';
  const margin = Math.abs(breakdown.margin);
  return `${breakdown.winner === BLACK ? 'B' : 'W'}+${margin % 1 === 0 ? margin : margin.toFixed(1)}`;
}
